// Recording what was done to a world, and doing it again.
//
// Phase 5 of design/09_Roadmap.md, and it is nearly free because of two earlier
// decisions rather than because of anything here. `tick(dt)` takes elapsed time as
// an argument (Phase 0, step 5), so time is a command like any other rather than
// something only a running clock can produce; and the marked source lives in
// `WorldState` (step 2), so "mark, walk, cast" is three pieces of world state
// rather than two plus a UI variable that a replay would have no way to restore.
//
// Two uses, and they want different things from the same artifact:
//
// **Debugging.** Something went wrong after a particular sequence; a recording is
// that sequence, replayable as many times as it takes, against a starting state
// that cannot drift.
//
// **Regression testing.** A recording carries the digest of the world it ended
// on, so replaying it later is a test: same commands, same starting state, same
// ending world, or a list of exactly what changed. That is a test of the *rules*
// rather than of the content -- the recording brings its own room, so re-authoring
// the shipped one does not silently invalidate it.
//
// A recording covers one room, because every command below is room-local: casts
// only ever act within the current room, decay only ticks the room the player
// occupies, and nothing here walks through a door. If the editor ever gains door
// traversal, this format's version is what says so.
import { Facing, RoomDef } from './types';
import { WorldState, createWorldState } from './state';
import { SimEvents } from './events';
import { PhysicsConfig, withPhysicsDefaults } from './physics';
import * as Casts from './casts';
import { ConceptId } from '../runes/concepts';
import { OperatorId } from '../runes/operators';
import { currentRuneIds } from '../runes/legacy';
import { settleWorld } from './settle';
import { advanceTurn, tick } from './tick';
import { CastOutcome, cast } from './cast';
import { applyInteract, applyUnlock, interactChangedTheWorld } from './interact';
import { readableJson } from './rooms/save';

export const RECORDING_FORMAT_VERSION = 1;

/** Everything that can be done *to* a world, as opposed to authored into one.
 * Placing an object is not here on purpose: that changes the starting state, not
 * the play, and a recording whose world changed halfway through would replay
 * against a world that never existed. */
export type SimCommand =
  | { kind: 'tick'; ms: number }
  | { kind: 'place-caster'; x: number; y: number }
  | { kind: 'step'; dir: Facing }
  | { kind: 'wait' }
  | { kind: 'face'; facing: Facing }
  | { kind: 'mark'; id: string | null }
  /** The non-cast verb -- open, read, pull, place. Names the object rather than
   * relying on what is faced, unlike `cast`: the game reaches it through the
   * faced target, but a recording that stored only "interact" would replay
   * against whatever happened to be in front of the caster, which is exactly
   * the fragility `facedObjectId` exists to avoid for casts by deriving it from
   * world state at record time. */
  | { kind: 'interact'; id: string }
  /** Using a carried key on the locked doorway at a tile. A tile rather than an
   * id because a `DoorDef` has none -- the doorway *is* the thing, and in the
   * game this is reached by walking into it. */
  | { kind: 'unlock'; x: number; y: number }
  | { kind: 'cast'; conceptId: ConceptId; operatorId: OperatorId; modifierIds: string[] };

/** The command kinds that never spend a turn, as data rather than as a fact
 * buried in a switch.
 *
 * Exported for one caller and it is worth naming which: the analysis search
 * bounds itself in *turns*, so it has to know before applying a move whether
 * that move can cost one. Reading this is how it avoids restating
 * `applyCommand`'s rule and drifting from it; `search.test.ts` pins the two
 * against each other by checking that a move built only from these leaves
 * `world.timeMs` alone.
 *
 * `step`, `wait` and `cast` are absent because they can spend a turn (`step`
 * only when something moved -- see its case). `tick` is absent for the opposite
 * reason: it does not advance a *turn*, but it is time passing by definition, so
 * calling it free would be a lie in the other direction. */
export const FREE_COMMANDS: ReadonlySet<SimCommand['kind']> = new Set(['face', 'mark', 'place-caster', 'interact', 'unlock']);

// `step` and `wait` were added 2026-08-12, alongside the turn clock, and
// `interact` 2026-08-13; the format version deliberately did *not* move with
// any of them.
//
// Nor did it move on 2026-08-14, when `interact` and `unlock` stopped costing a
// turn -- and that one is closer to the line, because it does change what an old
// recording *means*: a take containing an interact now replays without the decay
// tick that used to follow it. It is still not a bump, on the same arithmetic as
// the additions: the only committed take (`recordings/room4-ice-puzzle.json`)
// holds three casts, a mark and a tick, and the two kinds affected are one day
// old, so the recordings this could change do not exist. Bumping would refuse
// that take outright to protect a file nobody has.
//
// A version bump here is for a change that makes an old recording mean something
// different -- what this file's header names as the example, the day a recording
// can walk through a door and stop covering exactly one room. Adding a command
// kind is not that: no recording written before today contains a `step`, so every
// one of them still replays to exactly the world it recorded. Bumping would have
// cost that (the committed room4 take would be refused outright) to buy nothing.
//
// What it does leave open is the other direction -- a take recorded today, fed to
// a build that predates these two kinds, would skip them and diverge. That fails
// loudly rather than silently: `diffDigests` names every object that came out
// different, which is exactly the report a skipped step produces.

export interface Recording {
  formatVersion: number;
  /** The room as authored when recording started, carried in full so the replay
   * cannot be changed out from under it. */
  room: RoomDef;
  /** The numbers it was played on -- a recording made on a tuned config replays on
   * that config, not on today's defaults, or it would be replaying a different
   * simulation. */
  physics: PhysicsConfig;
  start: {
    x: number;
    y: number;
    facing: Facing;
    mana: number;
    health: number;
    charge: number;
    markedSourceId: string | null;
    /** The caster's warmth at the start. **Optional, and defaulted rather than
     * required**, for the reason `worldFromRecording` gives about
     * `withPhysicsDefaults`: a recording made before this field existed does not
     * *misread* it, so filling it in with `physics.player.thermalStart` -- which
     * is exactly where `createWorldState` would have put it -- replays such a
     * take precisely as it always did, and no format bump is owed.
     *
     * Added 2026-08-16, found by the game's own take download self-checking:
     * `worldDigest` has compared `thermalState` since it became a player
     * quantity, and `start` never carried it, so any take begun after the caster
     * had drifted off `thermalStart` replayed from the wrong warmth and diverged
     * on the one value nothing could put back. */
    thermalState?: number;
    /** How far into each ambient rule's own interval the world already was.
     * Same optional-and-defaulted treatment, and the same reason to want them:
     * `tick` carries these remainders between calls, so a take begun two thirds
     * of the way through a decay cycle fires its first decay a third of an
     * interval in, and a replay starting both at 0 fires it a whole interval in.
     * Nothing in `worldDigest` reads them directly -- what they move is *when*
     * everything else happens. */
    sinceManaRegenMs?: number;
    sinceRoomDecayMs?: number;
  };
  commands: SimCommand[];
  /** The world the recording ended on, for replay to check itself against.
   * Absent on a recording that was saved mid-take. */
  expected?: WorldDigest;
}

/** A comparable summary of a world: everything a command can change. Deliberately
 * the objects themselves rather than a hash -- when a replay diverges, "which
 * object, and how" is the whole question, and a hash cannot answer it. */
export interface WorldDigest {
  player: { x: number; y: number; facing: Facing; mana: number; health: number; charge: number; thermalState: number };
  markedSourceId: string | null;
  flowing: string[];
  objects: unknown[];
}

export function worldDigest(world: WorldState): WorldDigest {
  const room = world.rooms[world.player.roomId];
  const { x, y, facing, mana, health, charge, thermalState } = world.player;
  return {
    player: { x, y, facing, mana, health, charge, thermalState },
    markedSourceId: world.markedSource?.id ?? null,
    flowing: [...world.flowingTiles].sort(),
    objects: structuredClone(room.objects),
  };
}

/** Applies one command, settling afterwards exactly where the game does.
 *
 * The editor drives its own buttons through this same function, which is what
 * makes a replay faithful by construction rather than by two code paths agreeing:
 * there is only one implementation of "what a tick does", "what a cast does", and
 * a recording is the list of calls that were made to it.
 *
 * **Which commands cost a turn** (2026-08-12, revised 2026-08-14). The first
 * version of this rule was "the commands that settle are the commands that cost
 * a turn", which the function was already obeying by accident before there was a
 * turn clock to obey it for. That rule is now wrong in one place and the right
 * one is narrower: **a turn is spent by what the caster does to the world, not by
 * what they do with their hands.**
 *
 * `cast`, `step` and `wait` cost one. Everything else is free:
 *
 * - `face` and `mark`, because turning to look at something and marking a
 *   Transfer source change what the caster *knows*, not where anything is.
 * - `interact` and `unlock` (free since 2026-08-14, and the reason is the whole
 *   of it): **the game has never charged for either.** Opening a crate, reading
 *   a bookshelf, throwing a lever, placing a crystal and turning a key are all
 *   panel buttons in `game/src/ui.ts`, and none of them spends a turn or takes
 *   an undo point. So a recording or a search that charged for them was
 *   describing a clock the player is not on, and every turn count it produced
 *   for a room with a crate in it was one too many. They still *settle* --
 *   filling an altar has to reach `checkRuneAltars` -- which is what makes
 *   settling and turn cost two questions rather than one.
 * - `place-caster`, which is the editor standing the caster somewhere: an
 *   authoring act, not a move the caster made.
 * - `tick`, because it *is* time; charging it a turn as well would double-count.
 *
 * `FREE_COMMANDS` states the free half as data, so a caller that needs to know
 * before applying one (the analysis search, which bounds itself in turns) reads
 * the rule rather than restating it. */
export function applyCommand(world: WorldState, command: SimCommand, out: SimEvents): CastOutcome | null {
  switch (command.kind) {
    case 'tick': {
      tick(world, command.ms, out);
      // tick deliberately does not settle (see tick.ts): whether ambient decay can
      // invalidate a system is the caller's business. A replay has to assume it
      // can, since the room may be anything an editor authored.
      settleWorld(world, out);
      return null;
    }
    case 'place-caster': {
      const room = world.rooms[world.player.roomId];
      if (command.x < 0 || command.y < 0 || command.x >= room.cols || command.y >= room.rows) return null;
      // Written directly rather than through `movePlayerTo`, deliberately: this
      // is the editor standing the caster somewhere, not a step. It must not
      // burn them on a hazard they never walked onto, or drag a bound object
      // across the room after them.
      world.player.x = command.x;
      world.player.y = command.y;
      // But it does settle, for the same reason `movePlayerTo` now does: where
      // the caster stands is world state a system reads (`pad.forPlayer`), so a
      // placement that skipped this would leave the gate wired to a caster pad
      // reading the tile they were standing on a moment ago -- the editor
      // showing a world one mutation behind its own data.
      settleWorld(world, out);
      return null;
    }
    case 'step': {
      // The caster's own step, with every walk-into rule the game has -- pushing,
      // following a bind, redirecting through one -- because it is literally the
      // function the game calls (`Casts.walkPlayer`). Before this the game called
      // it directly and the editor had only `place-caster`, so a walk was the one
      // thing a player could do that a recording could not contain, which is
      // precisely the action a turn clock spends most of its turns on.
      //
      // Turn cost is decided by whether anything happened, not by walkPlayer's
      // return value, and the two differ in exactly one case worth paying for:
      // shoving a pushable object into flowing water moves the object and then
      // refuses to let the caster follow it onto the tile it just flooded
      // (see walkPlayer). Nothing moved the caster, so it returns false -- but a
      // Boulder is in the channel now, which is a turn's worth of the world
      // changing by anyone's reckoning. A walk into a wall, by contrast, returns
      // before touching anything at all and so emits nothing.
      const before = out.length;
      Casts.walkPlayer(world, command.dir, out);
      if (out.length > before) advanceTurn(world, out);
      return null;
    }
    case 'wait': {
      // The verb a turn clock has to invent. On the wall clock, waiting is what
      // the player does by not pressing anything, so there is nothing here to do;
      // once the world only moves when they do, letting a brazier refill or a
      // charge network relax stops being possible at all without a way to say
      // "spend a turn on purpose".
      advanceTurn(world, out);
      return null;
    }
    case 'face':
      world.player.facing = command.facing;
      return null;
    case 'mark': {
      if (command.id === null) {
        world.markedSource = null;
        return null;
      }
      const room = world.rooms[world.player.roomId];
      const object = room.objects.find((o) => o.id === command.id);
      // A mark deliberately stores a bare id/name pair rather than a reference
      // (see state.ts) -- marking something that is not here is exactly what the
      // game allows, and what puzzle.spec.ts:318 pins.
      world.markedSource = { id: command.id, name: object?.name ?? command.id };
      return null;
    }
    case 'interact': {
      // Settles when it did something -- filling an altar has to reach
      // `checkRuneAltars`, and taking an item that nothing reads must not drag
      // every system through a pass for nothing. Never advances the turn: see
      // this function's header. The game's interact button has never charged
      // one, and the simulation matching the game is the point.
      if (interactChangedTheWorld(applyInteract(world, command.id, out))) settleWorld(world, out);
      return null;
    }
    case 'unlock': {
      // Same shape and same reason: the key panel in `ui.ts` is a button, not a
      // move. Settles when the key actually turned, because a door that opened
      // is world state something else may read.
      if (applyUnlock(world, command.x, command.y).kind === 'unlocked') settleWorld(world, out);
      return null;
    }
    case 'cast': {
      const outcome = cast(
        world,
        {
          conceptId: command.conceptId,
          operatorId: command.operatorId,
          modifierIds: command.modifierIds,
          targetObjectId: facedObjectId(world),
          sourceObjectId: world.markedSource?.id ?? 'self',
        },
        out,
      );
      settleWorld(world, out);
      // Unconditionally, including a cast that fizzled or came up short: the
      // caster attempted it, and an attempt that cost Mana and achieved nothing
      // is exactly the kind of thing a turn is for. Making a failed cast free
      // would let a player probe the room for nothing, which is the brute-force
      // hole `16_Puzzle_Charter.md`'s closed-budget rule exists to shut.
      advanceTurn(world, out);
      return outcome;
    }
  }
}

/** A recording's commands, with rune ids brought up to their current spelling
 * (`runes/legacy.ts`).
 *
 * A take is written to somebody's disk and read back weeks later, so its
 * `modifierIds` are exactly as stale as the day it was recorded. Migrating here
 * rather than inside `applyCommand` keeps the running simulation free of the
 * question -- inside the process there is one spelling and it is the current one
 * -- and it is why this is a function rather than a field read: `replay` and
 * `analysis/trace.ts` both walk a recording's commands, and only one of them
 * going through the table would be worse than neither. */
export function recordedCommands(recording: Recording): SimCommand[] {
  return recording.commands.map((command) =>
    command.kind === 'cast' ? { ...command, modifierIds: currentRuneIds(command.modifierIds) } : command,
  );
}

/** Whatever the caster faces, or 'self' for open floor -- the target every cast
 * resolves against. Lives here rather than in the editor because a replay has to
 * derive it the same way the recording did, from world state alone. */
export function facedObjectId(world: WorldState): string {
  const offsets: Record<Facing, [number, number]> = { up: [0, -1], down: [0, 1], left: [-1, 0], right: [1, 0] };
  const [dx, dy] = offsets[world.player.facing];
  const x = world.player.x + dx;
  const y = world.player.y + dy;
  return world.rooms[world.player.roomId].objects.find((o) => o.x === x && o.y === y)?.id ?? 'self';
}

/** A world at a recording's starting state: its room, its numbers, its caster. */
export function worldFromRecording(recording: Recording): WorldState {
  if (recording.formatVersion !== RECORDING_FORMAT_VERSION) {
    throw new Error(`recording: format version ${recording.formatVersion}, expected ${RECORDING_FORMAT_VERSION}`);
  }
  // Defaults *under* the recording's own config, not instead of it: a recording
  // carries a whole PhysicsConfig by design (a take is a test of the rules, so
  // it must replay against the numbers it was played on), which means one taken
  // before a dial existed has no group for it at all. Filling only the missing
  // groups keeps every recorded value authoritative while stopping a new dial
  // from throwing on an old take. Found by charge.relaxRate (2026-08-08) doing
  // exactly that to the committed room4 recording.
  const world = createWorldState(withPhysicsDefaults(recording.physics));
  const room = structuredClone(recording.room);
  world.rooms = { [room.id]: room };
  world.player.roomId = room.id;
  world.player.x = recording.start.x;
  world.player.y = recording.start.y;
  world.player.facing = recording.start.facing;
  world.player.mana = recording.start.mana;
  world.player.health = recording.start.health;
  world.player.charge = recording.start.charge;
  world.player.thermalState = recording.start.thermalState ?? world.physics.player.thermalStart;
  world.sinceManaRegenMs = recording.start.sinceManaRegenMs ?? 0;
  world.sinceRoomDecayMs = recording.start.sinceRoomDecayMs ?? 0;
  world.markedSource = recording.start.markedSourceId
    ? { id: recording.start.markedSourceId, name: room.objects.find((o) => o.id === recording.start.markedSourceId)?.name ?? recording.start.markedSourceId }
    : null;
  const events: SimEvents = [];
  settleWorld(world, events);
  return world;
}

export interface ReplayResult {
  world: WorldState;
  digest: WorldDigest;
  events: SimEvents;
  /** Null when the recording carried no expectation to check against. */
  matches: boolean | null;
  /** Human-readable, and only as long as it needs to be: what came out different. */
  differences: string[];
}

/** Runs a recording from its own starting state and reports whether the world it
 * produced is the world it produced when it was recorded. */
export function replay(recording: Recording): ReplayResult {
  const world = worldFromRecording(recording);
  const events: SimEvents = [];
  for (const command of recordedCommands(recording)) applyCommand(world, command, events);
  const digest = worldDigest(world);
  const differences = recording.expected ? diffDigests(recording.expected, digest) : [];
  return { world, digest, events, matches: recording.expected ? differences.length === 0 : null, differences };
}

/** What changed between two digests, named rather than counted. */
export function diffDigests(expected: WorldDigest, actual: WorldDigest): string[] {
  const differences: string[] = [];
  const compare = (label: string, a: unknown, b: unknown) => {
    if (JSON.stringify(a) !== JSON.stringify(b)) differences.push(`${label}: expected ${JSON.stringify(a)}, got ${JSON.stringify(b)}`);
  };
  compare('player', expected.player, actual.player);
  compare('markedSource', expected.markedSourceId, actual.markedSourceId);
  compare('flowing tiles', expected.flowing, actual.flowing);

  const byId = (objects: unknown[]) => new Map(objects.map((o) => [(o as { id: string }).id, o]));
  const before = byId(expected.objects);
  const after = byId(actual.objects);
  for (const [id, object] of before) {
    if (!after.has(id)) differences.push(`${id}: expected to still exist, but it is gone`);
    else compare(id, object, after.get(id));
  }
  for (const id of after.keys()) {
    if (!before.has(id)) differences.push(`${id}: exists now, but did not when this was recorded`);
  }
  return differences;
}

/** A recording as a file, formatted the way world JSON is -- a recording embeds a
 * whole room, and one-number-per-line tilemaps would make it unreadable. */
export function stringifyRecording(recording: Recording): string {
  return readableJson(recording);
}
